/**
 * jv-partner-portal.js
 * Partner Portal (Planner / Referral Partner) — vanilla JS. Nav switching,
 * roster load + drawer, FAQ accordion, collateral form submit. Uses
 * window.jvBridge (snippets/jv-gas-bridge.liquid) for all GAS calls, and
 * window.jvPartnerAuth (snippets/jv-partner-auth.liquid) for identity.
 * Built 2026-07-20. Owner: Bridal Hub & Shopify Metafields Agent.
 */
(function () {
  'use strict';

  var STATE = {
    tier: '',
    customerEmail: '',
    customerId: '',
    brides: [],
    activeBrideEmail: ''
  };

  function ready(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  function esc(str) {
    return String(str == null ? '' : str).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function showToast(msg) {
    var t = document.getElementById('jv-partner-toast');
    if (!t) return;
    t.textContent = msg || 'Done';
    t.classList.add('show');
    setTimeout(function () { t.classList.remove('show'); }, 2600);
  }

  // SECURITY (2026-08-29, cross-tenant partner roster leak fix,
  // VPM-cross-tenant-partner-auth-fix-2026-08-29.md): was window.jvBridge.call(),
  // which sends straight to the Worker with no Shopify App-Proxy verification.
  // Every real partner request hit the GAS-side unverified fallback path,
  // resolved purely by client-supplied customerEmail/customerId with nothing
  // binding them together. window.jvBridge.callTeam() tries the App-Proxy path
  // first (Shopify's edge appends a verified logged_in_customer_id GAS cannot
  // be lied to about), falling back to the same direct path only when the proxy
  // is not configured/reachable. GAS-side (PartnerPortalAPI.gs) now independently
  // rejects any unverified customerId that does not resolve to the claimed email,
  // so this is defense-in-depth, not the only fix. Applied by the Vendor &
  // Partner Manager Agent (role 15) under the reversed-lead-rule for this file
  // (AGENT-TEAM.md role 15 boundary note), Bridal Hub agent (role 4) sign-off
  // implicit: single-line transport swap, no UI/markup change.
  function callBridge(route, payload) {
    payload = payload || {};
    payload.customerEmail = STATE.customerEmail;
    payload.customerId = STATE.customerId;
    if (!window.jvBridge || typeof window.jvBridge.callTeam !== 'function') {
      return Promise.resolve({ ok: false, error: 'bridge_unavailable' });
    }
    return window.jvBridge.callTeam(route, payload).catch(function (e) {
      return { ok: false, error: 'network_error', detail: e && e.message };
    });
  }

  // ── Nav ──────────────────────────────────────────────────────────────
  function setPane(name) {
    document.querySelectorAll('.portal-nav .nav-btn').forEach(function (b) { b.classList.remove('active'); });
    document.querySelectorAll('.portal-pane').forEach(function (p) { p.classList.remove('active'); });
    var btn = document.querySelector('.portal-nav .nav-btn[data-pane="' + name + '"]');
    var pane = document.getElementById('pane-portal-' + name);
    if (btn) btn.classList.add('active');
    if (pane) pane.classList.add('active');
  }

  function wireNav() {
    document.querySelectorAll('.portal-nav .nav-btn').forEach(function (btn) {
      btn.addEventListener('click', function () { setPane(btn.dataset.pane); });
    });
  }

  function applyTierToNav() {
    var rosterNavBtn = document.getElementById('jv-partner-nav-roster');
    if (STATE.tier === 'referral_partner') {
      if (rosterNavBtn) rosterNavBtn.classList.add('jv-hidden');
      setPane('resources');
    } else {
      if (rosterNavBtn) rosterNavBtn.classList.remove('jv-hidden');
    }
  }

  // ── Session + roster ─────────────────────────────────────────────────
  function loadSession() {
    var auth = window.jvPartnerAuth || {};
    STATE.tier = auth.tier || '';
    STATE.customerEmail = auth.email || '';
    STATE.customerId = auth.customerId || '';

    var nameEl = document.getElementById('jv-partner-who-name');
    var avatarEl = document.getElementById('jv-partner-avatar');
    if (nameEl) nameEl.textContent = auth.name || auth.email || 'Partner';
    if (avatarEl) avatarEl.textContent = (auth.name || auth.email || '?').substring(0, 2).toUpperCase();

    applyTierToNav();

    if (STATE.tier === 'planner') {
      // CDN fast path first (instant paint from partner_profile_cache via
      // the Cloudflare relay, zero GAS calls), then ALWAYS still run the
      // existing GAS-backed loadRoster() as a background refresh -- see the
      // fast-path function's own comment for the full rationale. A cache
      // miss here (flag not yet enabled, no cached profile, network issue)
      // just means loadRoster() runs exactly as it always has, so this is
      // purely additive and cannot make the existing path any worse.
      tryPartnerDashboardCdnFastPath_().then(function (cached) {
        var paintedFromCache = false;
        if (cached) {
          try {
            STATE.brides = cached.brides || [];
            renderRoster();
            renderKpis(cached.summary || {});
            var loadingEl = document.getElementById('jv-partner-roster-loading');
            if (loadingEl) loadingEl.classList.add('jv-hidden');
            paintedFromCache = true;
          } catch (paintErr) {
            try { console.warn('[jv-partner-portal] fast-path paint threw', paintErr); } catch (_) {}
          }
        }
        loadRoster(paintedFromCache);
      });
    }

    confirmSessionInBackground_();
  }

  // Reconciles the Liquid-set window.jvPartnerAuth (fast, tag-derived) against
  // the live CRM row via bridge_partner_getSession, in the background, after
  // first paint. Every route independently re-derives tier server-side
  // (partnerPortal_requireTier_), so this is not a security check - it just
  // keeps the displayed name/tier from drifting if the CRM record changed
  // since the Shopify tag was last set, and gives referral-partners (who
  // otherwise never hit a server route until they submit the collateral
  // form) an early server-confirmed identity check.
  function confirmSessionInBackground_() {
    callBridge('bridge_partner_getSession', {}).then(function (res) {
      if (!res || !res.ok || !res.partner) return;
      var p = res.partner;
      if (p.tier && p.tier !== STATE.tier) {
        STATE.tier = p.tier;
        applyTierToNav();
        if (STATE.tier === 'planner' && !STATE.brides.length) loadRoster();
      }
      var nameEl = document.getElementById('jv-partner-who-name');
      if (nameEl && p.businessName) nameEl.textContent = p.businessName;
    }).catch(function () { /* non-fatal - Liquid-tag state already painted */ });
  }

  function statusBadge(bride) {
    if (bride.booked) return { cls: 'status-badge--complete', label: 'Booked' };
    if (bride.proposalStatus && /sent|active/i.test(bride.proposalStatus)) {
      return { cls: 'status-badge--pending', label: 'Proposal Sent: Awaiting Response' };
    }
    return { cls: 'status-badge--notstarted', label: 'Not Started' };
  }

  function formatDate(iso) {
    if (!iso) return 'Enquiry only';
    var d = new Date(iso);
    if (isNaN(d.getTime())) return iso;
    var opts = { weekday: 'short', day: 'numeric', month: 'long', year: 'numeric' };
    return d.toLocaleDateString('en-AU', opts);
  }

  // Roster load error state (2026-09-02, BRIEFS/HANDOFF-vpm-roster-empty-error-states-2026-09-01.md).
  // Was previously indistinguishable from a genuinely empty roster: a failed
  // bridge_partner_getDashboard call only fired a 2.6s toast and left
  // #jv-partner-roster-grid blank, exactly what a zero-referrals partner also
  // sees. This makes a load failure visibly distinct, persistent (the toast
  // still fires too, as an immediate complement, not a replacement), with a
  // real retry that calls loadRoster() again rather than a second
  // parallel implementation.
  function showRosterError_() {
    var grid = document.getElementById('jv-partner-roster-grid');
    var empty = document.getElementById('jv-partner-roster-empty');
    var errorEl = document.getElementById('jv-partner-roster-error');
    if (grid) { grid.innerHTML = ''; grid.classList.add('jv-hidden'); }
    if (empty) empty.classList.add('jv-hidden');
    if (errorEl) errorEl.classList.remove('jv-hidden');
  }
  function hideRosterError_() {
    var errorEl = document.getElementById('jv-partner-roster-error');
    if (errorEl) errorEl.classList.add('jv-hidden');
  }

  function renderRoster() {
    var grid = document.getElementById('jv-partner-roster-grid');
    var empty = document.getElementById('jv-partner-roster-empty');
    if (!grid) return;
    // A successful render, even a zero-brides one, means the load genuinely
    // worked, so any error state left over from a prior failed attempt (this
    // retry, or an earlier session) is stale and must clear.
    hideRosterError_();
    grid.innerHTML = '';

    if (!STATE.brides.length) {
      grid.classList.add('jv-hidden');
      if (empty) empty.classList.remove('jv-hidden');
      return;
    }
    grid.classList.remove('jv-hidden');
    if (empty) empty.classList.add('jv-hidden');

    STATE.brides.forEach(function (bride) {
      var badge = statusBadge(bride);
      var card = document.createElement('div');
      // Next-milestone attention treatment (2026-09-02): the backend already sorts
      // needs-attention brides to the top of STATE.brides (PartnerPortalAPI.gs
      // partnerPortal_deriveNextMilestone_ / the roster-sort it feeds), this just
      // gives that ordering a matching visual signal so it isn't only noticeable
      // by position.
      var needsAttention = !!(bride.nextMilestone && bride.nextMilestone.needsAttention);
      card.className = 'roster-card' + (needsAttention ? ' needs-attention' : '');
      card.setAttribute('data-bride-email', bride.brideEmail);
      // ACCESSIBILITY (2026-08-30, checklist item 6): was click-only, keyboard-
      // only users could not open any bride's detail drawer at all.
      card.setAttribute('role', 'button');
      card.setAttribute('tabindex', '0');
      card.setAttribute('aria-label', 'View details for ' + (bride.brideName || 'unnamed couple'));
      var milestoneText = bride.nextMilestone && bride.nextMilestone.text;
      card.innerHTML =
        '<div class="top">' +
          '<div>' +
            '<div class="names">' + esc(bride.brideName || 'Unnamed couple') + '</div>' +
            '<div class="date">' + esc(formatDate(bride.weddingDate)) + '</div>' +
            '<div class="venue">' + esc(bride.weddingLocation || 'Not yet confirmed') + '</div>' +
          '</div>' +
          '<div class="chevron">&rsaquo;</div>' +
        '</div>' +
        '<span class="status-badge ' + badge.cls + '">' + esc(badge.label) + '</span>' +
        (milestoneText ? '<div class="milestone">' + esc(milestoneText) + '</div>' : '');
      card.addEventListener('click', function () { openDrawer(bride.brideEmail); });
      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          openDrawer(bride.brideEmail);
        }
      });
      grid.appendChild(card);
    });
  }

  function renderKpis(summary) {
    var totalEl = document.getElementById('jv-partner-kpi-total');
    var bookedEl = document.getElementById('jv-partner-kpi-booked');
    var proposalEl = document.getElementById('jv-partner-kpi-proposal');
    if (totalEl) totalEl.textContent = summary.total != null ? summary.total : '0';
    if (bookedEl) bookedEl.textContent = summary.booked != null ? summary.booked : '0';
    if (proposalEl) proposalEl.textContent = summary.proposalSent != null ? summary.proposalSent : '0';

    // Needs Attention 4th tile (2026-09-02). Hidden entirely when 0 rather than
    // shown as a permanent zero, a quiet week should not manufacture a
    // warning-coloured tile that trains the partner to ignore it.
    var attentionCard = document.getElementById('jv-partner-kpi-attention-card');
    var attentionEl = document.getElementById('jv-partner-kpi-attention');
    var attentionCount = summary.needsAttention != null ? summary.needsAttention : 0;
    if (attentionEl) attentionEl.textContent = attentionCount;
    if (attentionCard) {
      if (attentionCount > 0) attentionCard.classList.remove('jv-hidden');
      else attentionCard.classList.add('jv-hidden');
    }
  }

  // Defence-in-depth hard ceiling (BRIEF-planner-roster-stuck-loading-critical-
  // 2026-08-18). A live Cowork walkthrough found the roster spinner could get
  // stuck forever with NO error shown, even though the underlying
  // bridge_partner_getDashboard call eventually returned HTTP 200 (9-17s).
  // Static tracing could not pin the exact drop point (candidates: a JSON
  // parse failure in jv-gas-bridge.liquid's transport layer under load, or an
  // uncaught exception inside this function's own render step); this needs a
  // live breakpoint to confirm. Regardless of root cause, a planner must NEVER
  // be able to see an infinite spinner with no recovery path, so:
  //   1. a hard client-side timeout hides the spinner and shows the existing
  //      error toast if the call has not resolved within LOAD_ROSTER_TIMEOUT_MS
  //      (comfortably above the documented ~7-10s GAS cold-start floor and the
  //      observed 9-17s real response time);
  //   2. the render step is wrapped in try/catch so a rendering exception can
  //      never silently leave the spinner up (it previously ran unguarded
  //      AFTER the spinner was already hidden, so this specific ordering was
  //      not the bug, but a defensive wrap costs nothing and rules it out for
  //      good).
  // RAISED 2026-09-03 (BRIEFS/RESULTS-roster-diag-console-read-2026-09-03.md): the
  // ROSTER-DIAG diagnostic caught this timeout firing FALSE on two separate live
  // loads. bridge_partner_getDashboard genuinely resolved successfully both times,
  // just late (28.3s and 36.6s total elapsed, both past the old 25000ms ceiling).
  // Root cause is not a dropped or hung call, it's GAS's documented ~7-10s
  // cold-start floor stacked on a full CRM sheet scan (getDataRange().getValues()
  // over the whole bride sheet) when partner_profile_cache misses in
  // shopifyBridge_partnerGetDashboard (PartnerPortalAPI.gs). paintedFromCache
  // already stops this from showing a user-facing error when the Cloudflare fast
  // path hits (the case both diagnostic runs happened to be in), but a genuinely
  // cold/new partner with no fast-path cache would hit this timeout for real and
  // see an error even though the call was still going to succeed. 40000ms gives
  // headroom above the two observed real completions (28350ms, 36587ms) without
  // materially changing the already-established "planner must never see an
  // infinite spinner" ceiling behaviour above.
  var LOAD_ROSTER_TIMEOUT_MS = 40000;

  /* ── Partner Dashboard CDN fast-path (2026-09-02) ──────────────────────
     HANDOFF-vpm-partner-dashboard-cdn-fastpath-cloudflare-2026-09-02.md.
     bridge_partner_getDashboard already checks partner_profile_cache
     server-side, but that check happens AFTER GAS's ~7-10s cold-start
     floor, so a real live test measured 27.1s end to end -- past this
     file's own 25s LOAD_ROSTER_TIMEOUT_MS, so the page visibly times out
     on a normal load even on a cache hit. This gives the frontend a true
     zero-GAS-call read: a direct call to the Cloudflare relay's App-Proxy
     -verified /partner/partner-dashboard/read entity (cloudflare-cors-
     relay/worker.js PROXY_ENTITIES), which resolves the caller's identity
     from the Shopify-signed proxy params (a partner cannot spoof another
     partner's identity through this path, same trust chain
     bridge_partner_getSession already uses) and reads the SAME
     partner_profile_cache metaobject directly via the Storefront API --
     no client-side salt, no client-side Storefront token, no client-side
     crypto (see the handoff §2 for why the admin dashboard's client-side-
     salt pattern is UNSAFE to reuse here; this portal's whole audience is
     the group that pattern's own security comment says must never hold
     the salt).

     CORRECTED 2026-09-03 (BRIEFS/HANDOFF-partner-roster-root-cause-and-app-proxy-500-summary-
     2026-09-03.md): this was staged dark, but PARTNER_DASHBOARD_READ_ENABLED is now "1" in
     DEPOLYMENT/jv-crm-mirror/wrangler.toml (confirmed by direct grep of committed source,
     2026-09-03) -- the flag is LIVE, not unset. Live evidence backs this up too: real
     /partner/partner-dashboard/read reads measured 0.8-1.6s in production against the one real
     partner-managed account, and correctly painted the roster before the slow GAS background
     refresh completed. Still self-healing exactly as designed if the flag or the cache is ever
     unset/cold: a miss/error here falls straight through to loadRoster()'s own handling with no
     user-facing failure state of its own (see the rest of this comment, unchanged below).
     Mirrors abpLoadPartnerRoster_'s
     stale-while-revalidate shape (assets/jv-admin-bride-profile-v2.js): try
     the fast path, paint immediately on a hit, then ALWAYS still run the
     existing GAS route in the background so a just-added wedding still
     shows up within the session and any authoritative discrepancy self-
     corrects. A cache miss/error here is silent (falls straight through to
     loadRoster()'s own toast/error handling) -- this path has no user-
     facing failure state of its own, by design, per the handoff's "FAILED
     is not EMPTY" principle: a miss here is not a load failure, it's just
     "no fast path available right now," and the real load still runs. */
  function tryPartnerDashboardCdnFastPath_() {
    var base = window._jvPartnerProxyBase;
    if (!base || !STATE.customerEmail) return Promise.resolve(null);
    var nf = (window._jvNativeFetch || window.fetch).bind(window);
    var url = String(base).replace(/\/+$/, '') + '/partner/partner-dashboard/read';
    return nf(url, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify({})
    })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        if (res && res.ok && Array.isArray(res.brides)) return res;
        return null;
      })
      .catch(function () { return null; });
  }

  // paintedFromCache (2026-09-02, partner-dashboard fast-path addition):
  // true when tryPartnerDashboardCdnFastPath_() already painted real roster
  // data before this call. Two things change when it's true, same "FAILED
  // is not EMPTY" principle abpLoadPartnerRoster_ already applies for the
  // admin equivalent: (1) never re-show the loading spinner over data
  // that's already correctly on screen, and (2) a subsequent error/timeout
  // from THIS background-verify call must never wipe good cached data and
  // show an error state in its place -- leave the cache-painted roster up
  // and only log/toast quietly, since the fast path already answered the
  // question this call exists to answer.
  function loadRoster(paintedFromCache) {
    var loading = document.getElementById('jv-partner-roster-loading');
    if (loading && !paintedFromCache) loading.classList.remove('jv-hidden');

    // ROSTER-DIAG (2026-09-02, temporary — added to root-cause the roster
    // hang bug per BRIEFS/BUG-partner-roster-hangs-with-healthy-network-2026-09-02.md.
    // Remove this block (and the two other ROSTER-DIAG lines below) once the
    // drop point is confirmed and fixed.
    var _diagStart = Date.now();
    try { console.log('[ROSTER-DIAG] loadRoster: call starting, paintedFromCache=' + !!paintedFromCache + ', t=0ms'); } catch (_) {}

    var settled = false;
    var hardTimeout = setTimeout(function () {
      if (settled) return;
      settled = true;
      if (loading) loading.classList.add('jv-hidden');
      try { console.warn('[jv-partner-portal] loadRoster: hard timeout fired after ' + LOAD_ROSTER_TIMEOUT_MS + 'ms, bridge_partner_getDashboard never resolved in time.'); } catch (_) {}
      try { console.warn('[ROSTER-DIAG] loadRoster: TIMEOUT branch fired, actual elapsed=' + (Date.now() - _diagStart) + 'ms (configured=' + LOAD_ROSTER_TIMEOUT_MS + 'ms)'); } catch (_) {}
      if (paintedFromCache) return; // cache-painted data stays up, no error banner over it
      showToast("Couldn't load your couples, try refreshing.");
      showRosterError_();
    }, LOAD_ROSTER_TIMEOUT_MS);

    callBridge('bridge_partner_getDashboard', {}).then(function (res) {
      try { console.log('[ROSTER-DIAG] loadRoster: success callback entered, elapsed=' + (Date.now() - _diagStart) + 'ms, settled=' + settled + ', res=', res); } catch (_) {}
      if (settled) return; // hard timeout already fired and told the planner, do not double-handle
      settled = true;
      clearTimeout(hardTimeout);
      if (loading) loading.classList.add('jv-hidden');
      if (!res || !res.ok) {
        try { console.warn('[jv-partner-portal] loadRoster: resolved but not ok', res); } catch (_) {}
        // not_available_on_your_account_type is NOT a load failure, it is a
        // legitimate account-type restriction (a referral-tier partner has no
        // roster at all). Retrying will never fix it, so it stays a toast
        // only, does not use the error-banner-with-retry treatment below,
        // that would wrongly imply the roster is fetchable if she just tries
        // again.
        if (res && res.error === 'not_available_on_your_account_type') {
          if (!paintedFromCache) showToast('Roster is not available on your account type.');
          return;
        }
        if (paintedFromCache) return; // cache-painted data stays up, no error banner over it
        showToast("Couldn't load your couples, try refreshing.");
        showRosterError_();
        return;
      }
      try {
        STATE.brides = res.brides || [];
        renderRoster();
        renderKpis(res.summary || {});
      } catch (renderErr) {
        try { console.error('[jv-partner-portal] loadRoster: render threw', renderErr); } catch (_) {}
        if (paintedFromCache) return; // cache-painted data stays up, no error banner over it
        showToast("Couldn't load your couples, try refreshing.");
        showRosterError_();
      }
    });
  }

  // ── Bride detail drawer ──────────────────────────────────────────────
  function openDrawer(brideEmail) {
    STATE.activeBrideEmail = brideEmail;
    var overlay = document.getElementById('jv-partner-drawer-overlay');
    var drawer = document.getElementById('jv-partner-drawer');
    if (overlay) overlay.classList.add('open');
    if (drawer) drawer.classList.add('open');
    switchDrawerTab('overview');
    loadBrideSummary(brideEmail);
  }

  function closeDrawer() {
    var overlay = document.getElementById('jv-partner-drawer-overlay');
    var drawer = document.getElementById('jv-partner-drawer');
    if (overlay) overlay.classList.remove('open');
    if (drawer) drawer.classList.remove('open');
    STATE.activeBrideEmail = '';
  }

  function switchDrawerTab(pane) {
    document.querySelectorAll('.detail-drawer .drawer-tab').forEach(function (t) { t.classList.remove('active'); });
    document.querySelectorAll('.detail-drawer .drawer-pane').forEach(function (p) { p.classList.remove('active'); });
    var tabBtn = document.querySelector('.detail-drawer .drawer-tab[data-pane="' + pane + '"]');
    var paneEl = document.getElementById('jv-partner-pane-' + pane);
    if (tabBtn) tabBtn.classList.add('active');
    if (paneEl) paneEl.classList.add('active');
    if (pane === 'runsheet' && STATE.activeBrideEmail) loadRunsheetPdf(STATE.activeBrideEmail);
    if (pane === 'notes' && STATE.activeBrideEmail) loadNoteLog(STATE.activeBrideEmail);
  }

  // Renders the backend's partner-safe 3-state invoice vocabulary
  // (issued/paid/owing, see partnerPortal_deriveInvoiceStatus_ in
  // PartnerPortalAPI.gs) plus the visible amount.
  function formatInvoiceStatus_(invoiceStatus, amount) {
    var amt = String(amount || '').trim();
    var amtSuffix = amt ? (', ' + (amt.charAt(0) === '$' ? amt : ('$' + amt))) : '';
    if (invoiceStatus === 'paid') return 'Paid' + amtSuffix;
    if (invoiceStatus === 'owing') return 'Owing' + amtSuffix;
    if (invoiceStatus === 'issued') return 'Invoiced' + amtSuffix;
    return 'No invoice yet';
  }

  function loadBrideSummary(brideEmail) {
    document.getElementById('jv-partner-drawer-name').textContent = 'Loading…';
    callBridge('bridge_partner_getBrideSummary', { brideEmail: brideEmail }).then(function (res) {
      if (!res || !res.ok) {
        showToast("Couldn't load that couple's details.");
        closeDrawer();
        return;
      }
      var b = res.bride;
      document.getElementById('jv-partner-drawer-name').textContent = b.brideName || 'Unnamed couple';
      document.getElementById('jv-partner-drawer-meta').textContent =
        formatDate(b.weddingDate) + (b.weddingLocation ? ' · ' + b.weddingLocation : '');
      var badge = document.getElementById('jv-partner-drawer-status-badge');
      var isBooked = /yes/i.test(b.bookingStatus || '');
      badge.className = 'status-badge ' + (isBooked ? 'status-badge--complete' : 'status-badge--pending');
      badge.textContent = isBooked ? 'Booked' : (b.bookingStatus || 'In progress');

      document.getElementById('jv-partner-drawer-booking-status').textContent = b.bookingStatus || '—';
      document.getElementById('jv-partner-drawer-payment-status').textContent = formatInvoiceStatus_(b.invoiceStatus, b.partnerInvoiceAmount);
      document.getElementById('jv-partner-drawer-trial').textContent = b.trialDate ? ('Booked, ' + b.trialDate) : (b.trialRequired || '—');
      document.getElementById('jv-partner-drawer-services').textContent = b.servicesRequested || '—';
      document.getElementById('jv-partner-drawer-venue').textContent = b.weddingLocation || '—';

      loadNoteLog(brideEmail);
    });
  }

  // Sign-off state -> the same status-badge modifiers already used for booking
  // status above (--complete/--pending/--notstarted), so this reads as one
  // consistent vocabulary rather than a second badge style.
  function runsheetSignoffBadge_(signoffState, pendingReapprovalCount) {
    if (pendingReapprovalCount > 0) {
      return { cls: 'status-badge--pending', label: pendingReapprovalCount + ' change request' + (pendingReapprovalCount > 1 ? 's' : '') + ' pending review' };
    }
    if (signoffState === 'published') return { cls: 'status-badge--complete', label: 'Published' };
    if (signoffState === 'signed_off') return { cls: 'status-badge--complete', label: 'Signed off' };
    return { cls: 'status-badge--notstarted', label: 'Not signed off yet' };
  }

  /* =====================================================================
   * PARTNER RUNSHEET SIGN-OFF ACTION, shipped DARK (2026-09-07)
   * ---------------------------------------------------------------------
   * BRIEFS/BUILD-SCOPE-partner-runsheet-signoff-action-button-2026-09-07.md
   * (finding #9's action button, Runsheet Builder Agent). The status badge
   * above already SHOWS sign-off state; this is the missing click-to-approve
   * ACTION so a partner doesn't have to leave the portal to find the bride's
   * own token-link runsheet page just to sign off.
   *
   * Same identity/transport pattern as tryPartnerDashboardCdnFastPath_
   * above: native fetch via window._jvNativeFetch, the App-Proxy base URL
   * from window._jvPartnerProxyBase, no client-side secrets -- the partner's
   * identity is resolved server-side from the Shopify App-Proxy-signed
   * customer id, never client-supplied, same trust chain the live
   * 'partner-dashboard' PROXY_ENTITIES entry already uses. This IS the
   * first WRITE this file makes through the Worker rather than a read, but
   * the mechanism is identical to what's already live here.
   *
   * NOT LIVE YET. Genuinely inert until Cloudflare/Edge builds the
   * downstream Worker route (POST /runsheet-signoff on jv-crm-mirror) and
   * registers the 'runsheet-signoff' PROXY_ENTITIES row -- per this brief's
   * own "STILL OWED" split, neither exists as of this commit. Until then,
   * every call below 404s at the relay and falls straight into the existing
   * error toast, exactly like any other network failure this file already
   * handles -- no new failure mode, no half-built UI state. Do NOT wire the
   * button to render unconditionally before that lands; it's gated on
   * signoffState so it never even attempts to fire in a state that could
   * be a 404 mistaken for something more specific (see the gate below).
   *
   * Payload field names (customerEmail/customerId/brideEmail/signoffState)
   * match the schema migration's own documented contract exactly
   * (REBUILD-CLOUDFLARE/schema/060_published_runsheets_signoff.sql) --
   * signoff_state/signoff_at/signoff_by on the D1 side, the Worker route
   * resolves signoff_by='partner:<verified email>' server-side, never from
   * this payload (this file never sends a signoff_by field at all, by
   * design -- the identity binding is the Worker's job, not this page's).
   * =================================================================== */
  function signOffRunsheet_(brideEmail) {
    var base = window._jvPartnerProxyBase;
    if (!base || !STATE.customerEmail || !brideEmail) return Promise.resolve({ ok: false, error: 'not_configured' });
    var nf = (window._jvNativeFetch || window.fetch).bind(window);
    var url = String(base).replace(/\/+$/, '') + '/partner/runsheet-signoff/create';
    return nf(url, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain' },
      body: JSON.stringify({
        customerEmail: STATE.customerEmail,
        customerId: STATE.customerId,
        brideEmail: brideEmail
      })
    })
      .then(function (r) { return r.json(); })
      .then(function (res) {
        return (res && typeof res === 'object') ? res : { ok: false, error: 'bad_response' };
      })
      .catch(function () { return { ok: false, error: 'network' }; });
  }

  // Click handler for the sign-off button: disables the button immediately
  // (no double-submit), calls signOffRunsheet_, and on success re-runs
  // loadRunsheetPdf so the badge/date reflect the real post-write state from
  // the SAME read path the page already trusts -- never hand-paints a
  // "signed off" state from the write response alone.
  //
  // TWO SEPARATE LAG WINDOWS (clarified by Runsheet Builder, 2026-09-07 --
  // do not conflate these into one "the drain is slow" diagnosis later):
  //   1. The Worker's own D1 write (signoff_state on published_runsheets)
  //      is near-instant. loadRunsheetPdf today reads it INDIRECTLY, via
  //      bridge_partner_getRunsheetPdf -> partnerPortal_deriveSignoffState_,
  //      which derives its badge from CRM RUNSHEET_STAGE, NOT from D1 --
  //      so an immediate re-read reflects D1 only if/when this read path is
  //      migrated to read D1 directly (not done as of this commit).
  //   2. CRM RUNSHEET_STAGE itself only updates once the separate GAS-side
  //      reverse-sync drain (runsheetSignoffDrain_*, RunsheetOverhaul.gs)
  //      actually runs -- currently dry-run/manual, will eventually be a
  //      registered trigger. Admin-facing surfaces (the task/Chat alert
  //      pipelineTaskEngine_onRunsheetStage_ fires on a stage transition)
  //      depend on THIS lag, not the D1 write.
  // So a re-read a few seconds after a successful sign-off may still show
  // the pre-signoff badge -- that is expected today (lag window 2, the
  // drain), not a Worker bug and not lag window 1. If this read path is
  // later migrated to read D1 directly, re-check which of the two lags is
  // actually being observed before assuming it's still the drain.
  function handleSignoffClick_(brideEmail, btn) {
    if (!btn || btn.disabled) return;
    btn.disabled = true;
    var originalText = btn.textContent;
    btn.textContent = 'Signing off…';
    signOffRunsheet_(brideEmail).then(function (res) {
      if (res && res.ok) {
        showToast('Runsheet signed off');
        loadRunsheetPdf(brideEmail);
        return;
      }
      btn.disabled = false;
      btn.textContent = originalText;
      showToast("Couldn't sign off, try again.");
    });
  }

  function loadRunsheetPdf(brideEmail) {
    var slot = document.getElementById('jv-partner-runsheet-link-slot');
    if (!slot) return;
    slot.innerHTML = '<div class="jv-partner-loading" style="padding:1.5rem 0;"><div class="mini-spinner"></div> Checking runsheet…</div>';
    callBridge('bridge_partner_getRunsheetPdf', { brideEmail: brideEmail }).then(function (res) {
      if (!res) {
        slot.innerHTML = '<div class="empty-state" style="padding:2rem 0.5rem;"><p class="lead-paragraph" style="margin-bottom:0;">Runsheet not published yet.</p></div>';
        return;
      }
      var badge = runsheetSignoffBadge_(res.signoffState, res.pendingReapprovalCount || 0);
      var badgeHtml = '<span class="status-badge ' + badge.cls + '" style="margin-bottom:0.75rem;">' + esc(badge.label) + '</span>';
      var dateHtml = res.signoffDate ? '<div class="sub" style="margin-top:0.25rem;">' + esc(res.signoffDate) + '</div>' : '';

      // Sign-off action button: only when published, not already signed off,
      // and no pending reapproval (a bride-submitted change request should be
      // resolved before a partner can re-approve over it, matching this
      // file's existing badge precedence -- pendingReapprovalCount > 0 always
      // wins the badge display above too). Dark-flag gated separately from
      // the badge above: window.JV_PARTNER_RUNSHEET_SIGNOFF must be true, so
      // this can be turned on for testing independent of everything else on
      // the page, once Cloudflare/Edge's route exists. Off by default.
      var signoffBtnHtml = '';
      if (window.JV_PARTNER_RUNSHEET_SIGNOFF === true &&
          res.signoffState === 'published' &&
          (res.pendingReapprovalCount || 0) === 0) {
        signoffBtnHtml = '<button type="button" class="btn-primary jv-partner-signoff-btn" style="margin-top:0.75rem;">Sign off this runsheet</button>';
      }

      if (!res.ok) {
        slot.innerHTML = badgeHtml + '<div class="empty-state" style="padding:1rem 0.5rem;"><p class="lead-paragraph" style="margin-bottom:0;">Runsheet not published yet.</p></div>';
        return;
      }
      slot.innerHTML =
        badgeHtml +
        '<a href="' + esc(res.pdfUrl) + '" class="pdf-link" target="_blank" rel="noopener">' +
          '<span class="ic">&#9776;</span>' +
          '<div><div class="txt">View runsheet PDF</div><div class="sub">Opens in a new tab</div>' + dateHtml + '</div>' +
        '</a>' +
        signoffBtnHtml;

      var signoffBtn = slot.querySelector('.jv-partner-signoff-btn');
      if (signoffBtn) {
        signoffBtn.addEventListener('click', function () { handleSignoffClick_(brideEmail, signoffBtn); });
      }
    });
  }

  // Real note history (2026-09-16, H-04 from the 2026-08-13 vendor/partner
  // ecosystem audit). Previously this only cleared the DOM list, every note
  // in submitNote() below genuinely persists to COMMS_LOG server-side
  // (shopifyBridge_partnerLogNote, live), but reopening the drawer or
  // switching away and back showed an empty log, and a note added from a
  // different session/device was never visible at all. Backend route built
  // by the Vendor & Partner Manager Agent (PartnerPortalAPI.gs
  // shopifyBridge_partnerListNotes, commit ccacedec), reuses the same
  // comms_getUnifiedTimeline_ reader the write path's data already lives in,
  // not a new store. Response shape: { ok:true, notes:[{note,loggedAt,
  // partnerLabel}] }, newest first.
  function loadNoteLog(brideEmail) {
    var log = document.getElementById('jv-partner-note-log');
    if (!log) return;
    if (!brideEmail) { log.innerHTML = ''; return; }
    log.innerHTML = '<div class="jv-partner-loading" style="padding:1rem 0;"><div class="mini-spinner"></div> Loading notes...</div>';
    callBridge('bridge_partner_listNotes', { brideEmail: brideEmail }).then(function (res) {
      // Guard against a stale response landing after the drawer moved to a
      // different bride (fast tab/drawer switching); never paint the wrong
      // bride's notes into a log that's since been reused.
      if (STATE.activeBrideEmail !== brideEmail) return;
      if (!res || !res.ok) {
        log.innerHTML = '<div class="empty-state" style="padding:1rem 0.5rem;"><p class="lead-paragraph" style="margin-bottom:0;">Couldn\'t load notes.</p></div>';
        return;
      }
      var notes = res.notes || [];
      if (!notes.length) {
        log.innerHTML = '<div class="empty-state" style="padding:1rem 0.5rem;"><p class="lead-paragraph" style="margin-bottom:0;">No notes yet.</p></div>';
        return;
      }
      log.innerHTML = notes.map(function (n) {
        var dateLabel = '';
        if (n.loggedAt) {
          try {
            var d = new Date(n.loggedAt);
            if (!isNaN(d.getTime())) dateLabel = d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' });
          } catch (e) { /* leave dateLabel blank */ }
        }
        return '<div class="note-log-item">' +
          '<div class="meta"><span class="partner-tag">' + esc(n.partnerLabel || 'Partner note') + '</span>' +
          (dateLabel ? '<span>' + esc(dateLabel) + '</span>' : '') +
          '</div><div>' + esc(n.note || '') + '</div></div>';
      }).join('');
    });
  }

  function submitNote() {
    var input = document.getElementById('jv-partner-note-input');
    var note = input ? input.value.trim() : '';
    if (!note || !STATE.activeBrideEmail) return;
    callBridge('bridge_partner_logNote', { brideEmail: STATE.activeBrideEmail, note: note }).then(function (res) {
      if (!res || !res.ok) {
        showToast("Couldn't save your note, try again.");
        return;
      }
      var log = document.getElementById('jv-partner-note-log');
      if (log) {
        var item = document.createElement('div');
        item.className = 'note-log-item';
        item.innerHTML =
          '<div class="meta"><span class="partner-tag">Partner note</span><span>' +
          new Date().toLocaleDateString('en-AU', { day: 'numeric', month: 'short', year: 'numeric' }) +
          '</span></div><div>' + esc(note) + '</div>';
        log.insertBefore(item, log.firstChild);
      }
      if (input) input.value = '';
      showToast('Note added');
    });
  }

  function wireDrawer() {
    var closeBtn = document.getElementById('jv-partner-drawer-close');
    var overlay = document.getElementById('jv-partner-drawer-overlay');
    if (closeBtn) closeBtn.addEventListener('click', closeDrawer);
    if (overlay) overlay.addEventListener('click', closeDrawer);
    document.querySelectorAll('.detail-drawer .drawer-tab').forEach(function (tab) {
      tab.addEventListener('click', function () { switchDrawerTab(tab.dataset.pane); });
    });
    var noteBtn = document.getElementById('jv-partner-note-submit');
    if (noteBtn) noteBtn.addEventListener('click', submitNote);
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') closeDrawer();
    });
  }

  // ── FAQ accordion ────────────────────────────────────────────────────
  // ACCESSIBILITY (2026-08-30, checklist item 6): items are plain divs with
  // role="button"/tabindex="0" set in the Liquid markup, this shares one
  // toggle function between click and Enter/Space so keyboard-only users can
  // activate them, and keeps aria-expanded in sync for screen readers.
  function toggleFaqItem(item) {
    var isOpen = item.classList.toggle('open');
    item.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
  }

  function wireFaq() {
    document.querySelectorAll('[data-jv-faq-item]').forEach(function (item) {
      item.addEventListener('click', function () { toggleFaqItem(item); });
      item.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
          e.preventDefault();
          toggleFaqItem(item);
        }
      });
    });
  }

  // -- Collateral request form -------------------------------------------
  function wireCollateralForm() {
    var form = document.getElementById('jv-partner-collateral-form');
    if (!form) return;
    // A JS-level flag guards double-submit, not just submitBtn.disabled: on some
    // mobile browsers a queued tap can land before the disabled attribute takes
    // visual effect (RESULTS-vpm-collateral-form-frontend-fixes, Jess live-tested
    // on iPhone 2026-09-01, could not tell if her tap registered and tapped again).
    var submitting = false;
    form.addEventListener('submit', function (e) {
      e.preventDefault();
      if (submitting) return;
      // Data-driven catalogue (2026-09-02): reads every rendered quantity input
      // generically instead of two hardcoded ids, so a future item added to the
      // partner_collateral_item metaobject needs no JS change here. Each input
      // carries its own sku + item name via data attributes, set by the Liquid
      // loop in snippets/jv-partner-collateral-form.liquid. The composite
      // "name" sent to the backend matches the old hardcoded label shape
      // (item name + description in one string) so the request Jess sees in
      // her fulfilment view reads the same way as before this migration.
      var items = [];
      var qtyInputs = form.querySelectorAll('.jv-collateral-qty');
      qtyInputs.forEach(function (input) {
        var qty = parseInt(input.value || '0', 10) || 0;
        if (qty <= 0) return;
        var sku = input.getAttribute('data-collateral-sku') || '';
        var itemName = input.getAttribute('data-collateral-item-name') || '';
        var itemDescription = input.getAttribute('data-collateral-description') || '';
        if (!sku || !itemName) return;
        var composedName = itemDescription ? (itemName + ' (' + itemDescription + ')') : itemName;
        items.push({ sku: sku, name: composedName, qty: qty });
      });

      var address = ((document.getElementById('jv-collateral-address') || {}).value || '').trim();

      if (!items.length) { showToast('Choose at least one item.'); return; }
      if (!address) { showToast('Add a shipping address.'); return; }

      var submitBtn = form.querySelector('button[type="submit"]');
      var originalLabel = submitBtn ? submitBtn.textContent : '';
      submitting = true;
      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = 'Sending...';
      }

      callBridge('bridge_partner_requestCollateral', { items: items, shippingAddress: address }).then(function (res) {
        submitting = false;
        if (submitBtn) {
          submitBtn.disabled = false;
          submitBtn.textContent = originalLabel;
        }
        if (!res || !res.ok) {
          showToast("Couldn't send your request, try again.");
          return;
        }
        showToast('Request sent to Jessica');
        form.reset();
      });
    });
  }

  // Roster error-state "Try again" button (2026-09-02). Reuses loadRoster()
  // directly, not a second parallel implementation.
  function wireRosterRetry() {
    var btn = document.getElementById('jv-partner-roster-retry');
    if (btn) btn.addEventListener('click', function () { hideRosterError_(); loadRoster(); });
  }

  ready(function () {
    wireNav();
    wireDrawer();
    wireFaq();
    wireCollateralForm();
    wireRosterRetry();

    // window.jvPartnerAuth / window.jvBridge are set synchronously by their
    // respective snippets rendered before this script tag, but jv-gas-bridge's
    // async init can lag, so give it a tick before the first bridge call.
    //
    // DIAGNOSTIC (RESULTS-planner-roster-row1008-retest-2026-08-19.md, 2026-08-19):
    // a live retest against a correctly server-provisioned tier=planner account
    // found loadSession()/loadRoster() never ran at all, no network call, no
    // console output, no timeout, no error. This `if` had no `else` and nothing
    // logged inside loadSession() before it reaches loadRoster()'s own log lines,
    // so a false/missing window.jvPartnerAuth.loggedIn (or any synchronous throw
    // inside loadSession() itself) was completely invisible. Logging both branches
    // plus wrapping the deferred call in try/catch costs nothing and turns the
    // next live retest into a direct answer instead of another "nothing happened,
    // no idea why" report.
    if (window.jvPartnerAuth && window.jvPartnerAuth.loggedIn) {
      setTimeout(function () {
        try {
          console.warn('[jv-partner-portal] loadSession starting, tier=' + STATE.tier);
          loadSession();
        } catch (e) {
          try { console.error('[jv-partner-portal] loadSession threw', e); } catch (_) {}
        }
      }, 50);
    } else {
      try {
        console.warn('[jv-partner-portal] loadSession SKIPPED, window.jvPartnerAuth missing or not logged in', window.jvPartnerAuth);
      } catch (_) {}
    }
  });
})();
